import React, { useMemo } from 'react';
import type { NotebookCell } from './NotebookEditor';

interface TocEntry {
  cellId: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  cells: NotebookCell[];
  activeCellId: string | null;
  onNavigateToCell: (cellId: string) => void;
}

/**
 * Auto-generated table of contents from markdown heading cells.
 * Parses `# Heading` through `###### Heading` from markdown cell sources.
 */
export const TableOfContents: React.FC<TableOfContentsProps> = ({
  cells,
  activeCellId,
  onNavigateToCell,
}) => {
  const entries = useMemo<TocEntry[]>(() => {
    const result: TocEntry[] = [];
    for (const cell of cells) {
      if (cell.type !== 'markdown') continue;
      // Extract headings from each line
      for (const line of cell.source.split('\n')) {
        const match = line.match(/^(#{1,6})\s+(.+)/);
        if (match) {
          result.push({
            cellId: cell.id,
            text: match[2].trim(),
            level: match[1].length,
          });
        }
      }
    }
    return result;
  }, [cells]);

  if (entries.length === 0) {
    return (
      <div className="p-4 text-xs text-gray-500 italic">
        No headings found. Add markdown cells with # headings to build a table of contents.
      </div>
    );
  }

  return (
    <nav className="p-2 space-y-0.5">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 px-2 py-1 mb-1">
        Contents
      </div>
      {entries.map((entry, idx) => (
        <button
          key={`${entry.cellId}-${idx}`}
          onClick={() => {
            onNavigateToCell(entry.cellId);
            document.getElementById(`cell-${entry.cellId}`)?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }}
          className={`block w-full text-left px-2 py-1 text-xs truncate transition-colors ${
            entry.cellId === activeCellId
              ? 'text-blue-400 bg-blue-500/10'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          style={{ paddingLeft: `${(entry.level - 1) * 12 + 8}px` }}
          title={entry.text}
        >
          {entry.text}
        </button>
      ))}
    </nav>
  );
};
